import { getUnixTime, subDays } from 'date-fns'
import { Timestamp } from 'firebase/firestore'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import styled from 'styled-components'

import Page from 'commons/page'
import Block from 'components/block'
import ChatFloatingButton from 'components/chat-floating-button'
import { Horizontal } from 'components/layout'
import {
  PropertyListTitle,
  SectionTitle,
  StrongText,
  SubSectionTitle,
} from 'components/typography'
import { useAuthProvider } from 'contexts/auth'
import { useAppDispatch, useAppSelector, useIsSp } from 'hooks'
import AccessDenied from 'pages/access-denied'
import { UserRole } from 'schema/user'
import {
  fetchContractById,
  fetchFeatureById,
  selectContractById,
  selectContractByIdFailed,
  selectContractByIdLoading,
  selectFeatureById,
  selectFeatureByIdFailed,
  selectFeatureByIdLoading,
} from 'stores/contract'
import theme from 'styles/theme'
import { useQuery } from 'utils'
import PortalSiteGraph from '../../features/contracts/graph/portal-site-graph'
import MonthTally from '../../features/contracts/month-tally'
import { Contract, Feature } from '../../schema/contract'
import { useChatPath } from './hooks/useChatPath'

import { Row } from 'components/layout'
import NextActionArea from '../../features/contracts/next-action-area'
import DetailLinksComponent from './components/detail-links'
import ExternalLinksComponent from './components/external-links'
import { ExternalLink, ExternalLinks } from './components/external-links-base'
import PropertyExampleInfoArea from './components/property-example-info-area'
import { ContractDetailPanel, FeatureDetailPanel } from './other/detail-panel'

const GraphArea = styled(Horizontal)`
  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    flex-direction: column;
  }
`

const Spacer = styled.div`
  margin-bottom: 80px;
`

const OnlyPc = styled.div`
  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    display: none;
  }
`
const OnlySp = styled.div`
  @media only screen and (min-width: ${(p) => p.theme.vp + 1}px) {
    display: none;
  }
`

const PropertyInfoContainer = styled.div`
  padding: ${(p) => p.theme.space.l};
  background-color: ${(p) => p.theme.BACKGROUND_SURFACE};
  border-radius: 8px;
  box-shadow: none;
  box-sizing: border-box;
  border: 1px solid ${(p) => p.theme.BORDER};

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    padding: ${(p) => p.theme.space.l};
  }
`

const FixedPhraseContent = styled(Row)`
  align-items: center;
  gap: ${(p) => p.theme.space.s};

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    flex-direction: column;
    justify-content: center;
  }
`

const FixedPhraseLines = styled.div`
  display: flex;
  gap: 0;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    flex-direction: column;
    align-items: center;
    gap: 4px;
    line-height: 1.2;
  }
`

const FixedPhrase = styled(StrongText)`
  font-size: ${(p) => p.theme.font.size.l};
`

const FixedPhraseLink = ({
  message,
  parts,
  iconSrc,
  toChat,
}: {
  message: string
  parts: [string, string]
  iconSrc: string
  toChat: string
}) => (
  <ExternalLink
    as={Link}
    to={toChat}
    state={{ fixedMessage: message }}
    imageHeight="24px"
  >
    <FixedPhraseContent>
      <img src={iconSrc} alt={message} height="24px" />
      <FixedPhraseLines>
        <FixedPhrase size="md">{parts[0]}</FixedPhrase>
        <FixedPhrase size="md">{parts[1]}</FixedPhrase>
      </FixedPhraseLines>
    </FixedPhraseContent>
  </ExternalLink>
)

export const Wrapper = ({
  hideContainer = false,
  children,
}: {
  hideContainer?: boolean
  children: React.ReactNode
}) =>
  hideContainer ? (
    <>{children}</>
  ) : (
    <PropertyInfoContainer>{children}</PropertyInfoContainer>
  )

const ContractDetail = () => {
  const { claims } = useAuthProvider()
  const isSp = useIsSp(theme.vp)
  const contract = useAppSelector(selectContractById)
  const contractLoading = useAppSelector(selectContractByIdLoading)
  const contractFailed = useAppSelector(selectContractByIdFailed)
  const feature = useAppSelector(selectFeatureById)
  const featureLoading = useAppSelector(selectFeatureByIdLoading)
  const featureFailed = useAppSelector(selectFeatureByIdFailed)
  const dispatch = useAppDispatch()
  const queryString = useQuery()
  const type = queryString.get('type')
  const { id } = useParams<{ id: string }>()
  const toChat = useChatPath(claims?.role, id)
  const isOwner = useMemo(() => claims?.role === UserRole.OWNER, [claims?.role])

  useEffect(() => {
    if (!claims?.role) {
      return
    }

    if (!id) {
      return
    }

    if (type === 'feature') {
      if (claims?.role === UserRole.AGENT) {
        dispatch(fetchFeatureById({ id }))
      } else {
        // TODO 売り主がアクセスした場合の処理
      }
    } else {
      dispatch(fetchContractById({ role: claims?.role, id }))
    }
  }, [dispatch, claims?.role, type, id])

  const reFetch = () => {
    if (type === 'feature') {
      if (claims?.role === UserRole.AGENT) {
        dispatch(fetchFeatureById({ id: id! }))
      } else {
        // TODO 売り主がアクセスした場合の処理
      }
    } else {
      dispatch(fetchContractById({ role: claims!.role!, id: id! }))
    }
  }
  const data = useMemo(() => {
    return type === 'feature' ? feature : contract
  }, [contract, feature, type])

  const isContract = useMemo(() => {
    return type !== 'feature'
  }, [type])

  const pv = useMemo(() => {
    const monthAgo = getUnixTime(subDays(subDays(new Date(), 1), 30))
    const suumo = data?.suumo?.detailPv
      ?.filter((data) => {
        return data.date instanceof Timestamp
          ? data.date.toMillis()
          : data.date._seconds > monthAgo
      })
      .reduce((sum, data) => {
        return (sum += data.quantity)
      }, 0)
    const homes = data?.homes?.detailPv
      ?.filter((data) => {
        return data.date instanceof Timestamp
          ? data.date.toMillis()
          : data.date._seconds > monthAgo
      })
      .reduce((sum, data) => {
        return (sum += data.quantity)
      }, 0)
    const athome = data?.athome?.detailPv
      ?.filter((data) => {
        return data.date instanceof Timestamp
          ? data.date.toMillis()
          : data.date._seconds > monthAgo
      })
      .reduce((sum, data) => {
        return (sum += data.quantity)
      }, 0)

    return (suumo ?? 0) + (homes ?? 0) + (athome ?? 0)
  }, [data?.athome?.detailPv, data?.homes?.detailPv, data?.suumo?.detailPv])

  const reaction = useMemo(() => {
    const monthAgo = getUnixTime(subDays(subDays(new Date(), 1), 30))
    const suumoDocumentRequestSum = [...(data?.suumo?.documentRequest || [])]
      ?.filter((data) => {
        return data.date instanceof Timestamp
          ? data.date.toMillis()
          : data.date._seconds > monthAgo
      })
      .reduce((sum, data) => {
        return (sum += data.quantity)
      }, 0)

    const suumoVisitorReservationSum = [
      ...(data?.suumo?.visitorReservation || []),
    ]
      ?.filter((data) => {
        return data.date instanceof Timestamp
          ? data.date.toMillis()
          : data.date._seconds > monthAgo
      })
      .reduce((sum, data) => {
        return (sum += data.quantity)
      }, 0)

    const athomeDocumentRequestSum = [...(data?.athome?.documentRequest || [])]
      ?.filter((data) => {
        return data.date instanceof Timestamp
          ? data.date.toMillis()
          : data.date._seconds > monthAgo
      })
      .reduce((sum, data) => {
        return (sum += data.quantity)
      }, 0)

    return (
      suumoDocumentRequestSum +
      suumoVisitorReservationSum +
      athomeDocumentRequestSum
    )
  }, [
    data?.athome?.documentRequest,
    data?.suumo?.documentRequest,
    data?.suumo?.visitorReservation,
  ])

  const HasApiError = useMemo(() => {
    return contractFailed || featureFailed
  }, [contractFailed, featureFailed])

  const [detailAnnotation, setDetailAnnotation] = useState('')
  useEffect(() => {
    if (!contract) {
      return
    }

    let sites: string[] = []
    if (!contract.suumo) {
      sites.push('SUUMO')
    }

    if (!contract.homes) {
      sites.push("HOME's")
    }

    if (!contract.athome) {
      sites.push('athome')
    }

    if (sites.length > 0) {
      setDetailAnnotation(`この物件は${sites.join(',')}には掲載されていません`)
    } else {
      setDetailAnnotation('')
    }
  }, [contract])

  // ContractDetailPanel component moved to contract/other/contract-detail-panel.tsx

  const DetailPanel = () => {
    if (isContract && data) {
      return (
        <ContractDetailPanel
          data={data as Contract}
          isContract={isContract}
          isOwner={isOwner}
        />
      )
    }

    if (data) {
      return <FeatureDetailPanel data={data as Feature} />
    }

    return <></>
  }

  return (
    <>
      {HasApiError && <AccessDenied />}
      {!HasApiError && (
        <Page
          title="物件詳細"
          loading={contractLoading || featureLoading}
          type="user"
          showBackButton={true}
        >
          {/* 次に取るべきアクションの提案エリア */}
          <NextActionArea
            isContract={isContract}
            id={id!}
            suumoUnderAverageListPvPercent={
              isContract
                ? contract?.nextActionInfo?.suumoUnderAverageListPvPercent
                : feature?.nextActionInfo?.suumoUnderAverageListPvPercent
            }
            suumoUnderAverageTransitionRate={
              isContract
                ? contract?.nextActionInfo?.suumoUnderAverageTransitionRate
                : feature?.nextActionInfo?.suumoUnderAverageTransitionRate
            }
            refresh={reFetch}
          />
          <PropertyListTitle>物件詳細</PropertyListTitle>

          <Block>
            <SectionTitle>物件情報</SectionTitle>
            {/* 物件の基本情報と売主情報を表示するパネル */}
            <Wrapper hideContainer={isSp}>
              <DetailPanel />
            </Wrapper>
          </Block>
          <Block>
            <SectionTitle>各種ポータルサイト上での動き</SectionTitle>
            <Wrapper>
              <OnlyPc>
                <DetailLinksComponent
                  data={data ?? null}
                  type={type}
                  isContract={isContract}
                  isOwner={isOwner}
                  userRole={claims?.role || null}
                  detailAnnotation={detailAnnotation}
                  isSp={false}
                />
              </OnlyPc>
              <GraphArea justify="space-between">
                <PortalSiteGraph contract={data} />
                <MonthTally
                  list={[
                    {
                      label: '直近30日の表示数',
                      value: pv,
                      unit: '回',
                    },
                    {
                      label: '直近30日の反響数',
                      value: reaction,
                      unit: '回',
                    },
                  ]}
                />
              </GraphArea>
            </Wrapper>
          </Block>

          <Block>
            <OnlySp>
              <Wrapper>
                <SectionTitle>詳細データ</SectionTitle>
                {/* SP版: ポータルサイト詳細データへのリンク（縦並びリスト形式） */}
                <DetailLinksComponent
                  data={data ?? null}
                  type={type}
                  isContract={isContract}
                  isOwner={isOwner}
                  userRole={claims?.role || null}
                  detailAnnotation={detailAnnotation}
                  isSp={true}
                />
              </Wrapper>
            </OnlySp>
          </Block>
          {isOwner && (
            <Block>
              <SubSectionTitle>定型文送信</SubSectionTitle>
              <Wrapper>
                <ExternalLinks>
                  <FixedPhraseLink
                    message="売却価格を相談したい"
                    parts={['売却価格を', '相談したい']}
                    iconSrc="/image/bank-note.png"
                    toChat={toChat}
                  />
                  <FixedPhraseLink
                    message="掲載内容を相談したい"
                    parts={['掲載内容を', '相談したい']}
                    iconSrc="/image/list.png"
                    toChat={toChat}
                  />
                  <FixedPhraseLink
                    message="媒介契約を延長したい"
                    parts={['媒介契約を', '延長したい']}
                    iconSrc="/image/document-edit.png"
                    toChat={toChat}
                  />
                </ExternalLinks>
              </Wrapper>
            </Block>
          )}
          <Block>
            <SectionTitle>各種ポータルサイト上での表示</SectionTitle>
            {/* 各種ポータルサイト（SUUMO、AtHome、Homes）への外部リンク */}
            <Wrapper hideContainer={isSp}>
              <ExternalLinksComponent
                suumoLink={data?.suumo?.url}
                athomeLink={data?.athome?.url}
                homesLink={data?.homes?.url}
              />
            </Wrapper>
          </Block>
          {isContract && data && (
            <PropertyExampleInfoArea
              contractId={id!}
              config={
                (data as Contract).activeInventory?.config ||
                (data as Contract).successfullyConcludedDeal?.config
              }
              contract={data as Contract}
              userRole={claims?.role || null}
              isOwner={isOwner}
              onSuccess={() => reFetch()}
              reFetch={reFetch}
            />
          )}
          <Spacer />
          <ChatFloatingButton to={toChat} contract={data as Contract} />
        </Page>
      )}
    </>
  )
}

export default ContractDetail
